import { useFrame, useThree } from "@react-three/fiber";
import { saveFile, useNotifications } from "@tosize/ui";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { type PerspectiveCamera, Vector3 } from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useCabinetStore } from "../../../../context/StoreContext";
import { useCanvasStore } from "../../../../hooks/useCanvasStore";
import { deserialize, getCurrentMaterial, serialize } from "../../data";
import { useDebug } from "../../debug";
import { calculcateCameraZPosition } from "../../utils";

interface SecretControlsProps {
	controls: OrbitControlsImpl | null;
}

export function SecretControls({ controls }: SecretControlsProps) {
	const { gl, scene, camera } = useThree((three) => ({
		gl: three.gl,
		scene: three.scene,
		camera: three.camera,
	}));
	const { state, api, setStore, undo, redo, undoDisabled, redoDisabled } = useCabinetStore();
	const { fullScreen, setFullScreen, setAltControls, setShowDimensions, toggleDimensionUnit } =
		useCanvasStore();
	const { notify } = useNotifications();
	const intl = useIntl();
	const { debugEnabled, toggleDebug } = useDebug();
	const [rotation, setRotation] = useState({ x: 0, y: 0 });
	const material = getCurrentMaterial(state, api);

	const [width, height, depth] = [state.width / 1000, state.height / 1000, state.depth / 1000];

	useFrame((frame) => {
		if (fullScreen && controls) {
			frame.invalidate();
			frame.setDpr(2);

			const camera = frame.camera as PerspectiveCamera;

			frame.camera.lookAt(0, 0, 0);
			frame.camera.position.x = rotation.x;
			frame.camera.position.y = rotation.y;
			frame.camera.position.z = calculcateCameraZPosition(
				camera.fov,
				camera.aspect,
				new Vector3(width, height, depth),
			);

			frame.camera.rotation.set(0, 0, 0);
			frame.camera.updateProjectionMatrix();

			controls.target.set(0, 0, 0);
			controls.update();
		}
	});

	const downloadPicture = useCallback(
		async (rotation: { x: number; y: number }) => {
			setRotation(rotation);

			await new Promise((res) => setTimeout(res, 2500));

			gl.render(scene, camera);
			const dataUrl = gl.domElement.toDataURL();

			const name = (material?.name ?? "transparent").replace(/\s+/g, "-").toLowerCase();

			saveFile(dataUrl, `canvas-${name}-${rotation.x}-${rotation.y}.png`);
		},
		[material, scene, gl, camera],
	);

	const downloadGltf = useCallback(async () => {
		const { GLTFExporter } = await import("three/examples/jsm/exporters/GLTFExporter");

		const exporter = new GLTFExporter();
		const cabinet = scene.getObjectByName("Cabinet");

		if (!cabinet) {
			throw new Error("Cabinet not found");
		}

		exporter.parse(
			cabinet,
			// called when the gltf has been generated
			(result) => {
				if (result instanceof ArrayBuffer) {
					const blob = new Blob([result], { type: "application/octet-stream" });

					saveFile(blob, "scene.glb");
				} else {
					const output = JSON.stringify(result, null, 2);
					const blob = new Blob([output], { type: "text/plain" });

					saveFile(blob, "scene.gltf");
				}
			},
			// called when there is an error in the generation
			(error: ErrorEvent) => {
				console.error("An error happened", error);
			},
			{
				binary: false,
				trs: false,
			},
		);
	}, [scene]);

	const importStore = useCallback(async () => {
		try {
			const data = await navigator.clipboard.readText();
			const deserializedData = deserialize(data);

			setStore((state) => ({ ...state, ...deserializedData }));

			notify({
				title: intl.formatMessage({
					defaultMessage: "Klembord content geïmporteerd",
					id: "aVR4SY",
				}),
			});
		} catch {
			notify({
				variant: "warning",
				title: intl.formatMessage({
					defaultMessage: "Klembord content is geen geldige JSON data",
					id: "iynBA7",
				}),
			});
		}
	}, [intl, notify, setStore]);

	const exportStore = useCallback(async () => {
		const data = serialize(state);

		await navigator.clipboard.writeText(data);

		notify({
			title: intl.formatMessage({
				defaultMessage: "Gekopieerd naar klembord",
				id: "kXSYAH",
			}),
		});
	}, [intl, notify, state]);

	const onKeyDown = useCallback(
		async (event: KeyboardEvent) => {
			const inputs = ["input", "textarea"];
			const tagName =
				event.target instanceof HTMLElement ? event.target.tagName.toLowerCase() : undefined;
			const notFormElement = (tagName && !inputs.includes(tagName)) || !tagName;

			if (notFormElement) {
				if (event.shiftKey && (event.key === "P" || event.key === "A" || event.key === "G")) {
					if (debugEnabled) toggleDebug();
					setShowDimensions(event.key === "A");
					setAltControls(false);
					setFullScreen(true);

					if (event.key === "P") {
						await downloadPicture({ x: Math.min(1, width), y: Math.min(1, height) });
					}

					if (event.key === "A") {
						await downloadPicture({ x: 0, y: 0 });
					}

					if (event.key === "G") {
						await downloadGltf();
					}

					await new Promise((res) => setTimeout(res, 1000));

					setFullScreen(false);
				}

				if (event.shiftKey && event.key === "X") {
					toggleDebug();
				}

				if (event.shiftKey && event.key === "E") {
					await exportStore();
				}

				if (event.shiftKey && event.key === "I") {
					await importStore();
				}

				if (event.shiftKey && event.key === "U") {
					toggleDimensionUnit();
				}

				if (event.metaKey && !event.shiftKey && event.key === "z") {
					undoDisabled
						? notify({
								variant: "info",
								title: intl.formatMessage({
									defaultMessage: "Geen vorige versie van dit meubel beschikbaar",
									id: "Hm4y34",
								}),
							})
						: undo();
				}

				if (event.metaKey && event.shiftKey && event.key === "z") {
					redoDisabled
						? notify({
								variant: "info",
								title: intl.formatMessage({
									defaultMessage: "Geen volgende versie van dit meubel beschikbaar",
									id: "B7VFrP",
								}),
							})
						: redo();
				}
			}
		},
		[
			debugEnabled,
			toggleDebug,
			downloadGltf,
			downloadPicture,
			exportStore,
			height,
			importStore,
			intl,
			notify,
			redo,
			redoDisabled,
			toggleDimensionUnit,
			setFullScreen,
			setShowDimensions,
			setAltControls,
			undo,
			undoDisabled,
			width,
		],
	);

	useEffect(() => {
		window.addEventListener("keydown", onKeyDown);

		return () => {
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [onKeyDown]);

	return null;
}
